import React, { PureComponent } from 'react';
import { map } from 'lodash';
import classNames from 'classnames';

import { EventActionBarWithOutHOC as EventPageActionBar } from '@eb/event-create-primitives';
import { CalendarGraphic } from '@eb/eds-iconography';
import { CopyGraphic } from '@eb/eds-iconography';
import { CheckChunky } from '@eb/eds-iconography';
import { Graphic } from '@eb/eds-graphic';
import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';

import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Heading from '../../components/Heading';
import {
    AFTER_DOWNGRADE,
    CURRENT_PACKAGE,
    defaultDowngradePackage,
    DOWNGRADE_CTA,
    getFeatures,
    getHeading,
    KEEP_PROFESSIONAL_CTA,
    NEW_EVENTS_TEXT,
    NO_COPY_TEXT,
    TABLE_TITLE,
    TABLE_TITLE_SHORT,
} from '../../constants/downgrade';
import { PACKAGE_ONE_NAME, PACKAGE_TWO_NAME } from '../../constants/main';
import {
    GA_CHANGE_PLAN_CATEGORY,
    GA_CHOOSE_PACKAGE,
    GA_DOWNGRADE_PAGE_VIEW,
    GA_P2_LABEL,
    GA_PACKAGE_DOWNGRADED,
} from '../../constants/analytics';
import defaultSettings from './settings';
import transform from '../transform';
import {
    isActiveForAU,
    isEssentialsNewVersion,
    loadCrazyEgg,
    trackAnalyticsEventAU,
} from '../utils';

import './page.scss';

const TwoColumnGraphics = () => (
    <div className="eds-g-group eds-text--center">
        <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-1-2 eds-l-pad-hor-14 eds-l-pad-vert-8">
            <Graphic type={<CalendarGraphic />} size="small" />
            <p className="eds-text-hs eds-l-pad-hor-2">{NEW_EVENTS_TEXT}</p>
        </div>
        <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-1-2 eds-l-pad-hor-14 eds-l-pad-vert-8">
            <Graphic type={<CopyGraphic />} size="small" />
            <p className="eds-text-hs eds-l-pad-hor-2">{NO_COPY_TEXT}</p>
        </div>
    </div>
);

const CheckMark = () => (
    <Icon type={<CheckChunky />} size="small" strokeColor="ui-orange" />
);

const FeatureTableHeaderTitle = ({ title, subTitle }) => {
    let subTitleMarkup = '';

    if (subTitle) {
        subTitleMarkup = (
            <p className="eds-text-bm eds-text-weight--light eds-show-up-mn">
                {subTitle}
            </p>
        );
    }

    return (
        <div className="eds-text--center">
            <p className="eds-text-bl eds-text-weight--heavy eds-show-up-md">
                {title}
            </p>
            <p className="eds-text-bm eds-show-down-mn">{title}</p>
            {subTitleMarkup}
        </div>
    );
};

const FeatureTableRowItem = ({ children, highlightColumn = false }) => {
    const dynamicProps = {
        className: 'feature-table__row-item eds-l-pad-vert-4',
    };
    if (!children) {
        dynamicProps['dangerouslySetInnerHTML'] = {
            __html: '&mdash;',
        };
        return <div {...dynamicProps}>{children}</div>;
    }
    const highlightColumnClass = classNames({
        'feature-table__row-item_border': highlightColumn,
    });

    return (
        <div {...dynamicProps}>
            <div className={highlightColumnClass} />
            {children}
        </div>
    );
};

const FeatureTableRow = ({
    isHeader,
    feature,
    professional,
    essentials,
    highlightColumn,
}) => {
    const classes = classNames('feature-table__row', {
        'feature-table__header': isHeader,
    });

    return (
        <div className={classes}>
            <FeatureTableRowItem>{feature}</FeatureTableRowItem>
            <FeatureTableRowItem highlightColumn={highlightColumn}>
                {professional}
            </FeatureTableRowItem>
            <FeatureTableRowItem>{essentials}</FeatureTableRowItem>
        </div>
    );
};

const FeatureTableContents = ({ features }) => {
    const rows = map(features, ({ feature, professional, essentials }, key) => (
        <FeatureTableRow
            key={key}
            feature={feature}
            professional={professional || <CheckMark />}
            essentials={essentials}
        />
    ));

    return <div>{rows}</div>;
};

const FeatureTable = ({ features, highlightProfessional }) => {
    const featuresTitle = (
        <div>
            <div className="eds-show-down-mn">
                <FeatureTableHeaderTitle title={TABLE_TITLE_SHORT} />
            </div>
            <div className="eds-show-up-md">
                <FeatureTableHeaderTitle title={TABLE_TITLE} />
            </div>
        </div>
    );
    const professionalTitle = (
        <FeatureTableHeaderTitle
            title={PACKAGE_TWO_NAME}
            subTitle={CURRENT_PACKAGE}
        />
    );
    const essentialsTitle = (
        <FeatureTableHeaderTitle
            title={PACKAGE_ONE_NAME}
            subTitle={AFTER_DOWNGRADE}
        />
    );
    const tableWrapperClasses = classNames(
        'eds-g-group',
        'eds-l-pad-vert-14',
        'eds-l-pad-hor-4',
        {
            'feature-table__wrapper': highlightProfessional,
        },
    );

    return (
        <div className={tableWrapperClasses}>
            <div className="eds-g-cell eds-g-cell-1-1">
                <div className="feature-table eds-g-cell eds-g-cell-1-1">
                    <FeatureTableRow
                        isHeader={true}
                        feature={featuresTitle}
                        professional={professionalTitle}
                        essentials={essentialsTitle}
                        highlightColumn={highlightProfessional}
                    />
                    <FeatureTableContents features={features} />
                </div>
            </div>
        </div>
    );
};

const DowngradeCTA = ({ onDowngrade, onKeepPackage }) => (
    <div className="eds-g-group eds-l-pad-vert-14 eds-text--center">
        <div className="eds-g-cell eds-g-cell eds-g-cell-1-1 eds-g-cell-sw-1-2 eds-g-cell-md-4-12 eds-g-cell-mw-3-12 eds-l-pad-hor-2 eds-l-pad-vert-2">
            <Button
                onClick={onKeepPackage}
                size="block"
                style="fill"
                data-automation="downgrade-cancellation-button"
            >
                {KEEP_PROFESSIONAL_CTA}
            </Button>
        </div>
        <div className="eds-g-cell eds-g-cell eds-g-cell-1-1 eds-g-cell-sw-1-2 eds-g-cell-md-4-12 eds-g-cell-mw-3-12 eds-l-pad-hor-2 eds-l-pad-vert-2">
            <Button
                size="block"
                style="neutral"
                onClick={onDowngrade}
                data-automation="downgrade-confirmation-button"
            >
                {DOWNGRADE_CTA}
            </Button>
        </div>
    </div>
);

const ActionBarComponent = ({
    onKeepPackage,
    onDowngrade,
    useStandardActionBar,
}) =>
    useStandardActionBar ? (
        <div className="eds-l-pad-top-14">
            <EventPageActionBar
                visible={true}
                onDiscard={onKeepPackage}
                onSave={onDowngrade}
                submitText={DOWNGRADE_CTA}
                discardText={KEEP_PROFESSIONAL_CTA}
            />
        </div>
    ) : (
        <DowngradeCTA onKeepPackage={onKeepPackage} onDowngrade={onDowngrade} />
    );

export default class DowngradePage extends PureComponent {
    componentDidMount() {
        const { trackEvent } = this.props;

        trackEvent({
            action: GA_DOWNGRADE_PAGE_VIEW,
            label: GA_P2_LABEL,
            category: GA_CHANGE_PLAN_CATEGORY,
        });

        this._trackEventActionsAU();

        //Crazy Egg
        loadCrazyEgg();
    }
    _trackEventActionsAU() {
        const {
            assortmentsAvailability,
            locale,
            userId,
            userPlan,
        } = this.props;

        const version = assortmentsAvailability
            ? assortmentsAvailability.current.version
            : null;
        if (isActiveForAU(locale)) {
            trackAnalyticsEventAU({
                action: GA_PACKAGE_DOWNGRADED,
                category: GA_CHOOSE_PACKAGE,
                dimensions: {
                    userId,
                    userPlan,
                    version,
                    locale,
                },
            });
        }
    }

    render() {
        const {
            assortmentsAvailability,
            isPackageChangeDowngradeEnabled,
            locale,
            setPackageOne,
            onKeepPackage,
        } = this.props;
        const downgrade =
            assortmentsAvailability === null
                ? defaultDowngradePackage
                : assortmentsAvailability.downgrade;
        const { breadcrumbs } = transform({ defaultSettings });

        // Experiment in Australia is using a new version of essentials
        const useNewVersion = isEssentialsNewVersion(locale, downgrade);
        const firstHeading = getHeading(
            useNewVersion,
            isPackageChangeDowngradeEnabled,
        );

        return (
            <div>
                <HeaderBreadcrumbs {...breadcrumbs} />
                <div className="eds-g-grid marketing-container__max-width--wide">
                    <Heading {...firstHeading} />
                    {!isPackageChangeDowngradeEnabled ? (
                        <TwoColumnGraphics />
                    ) : null}
                    <FeatureTable
                        features={getFeatures(useNewVersion)}
                        highlightProfessional={isPackageChangeDowngradeEnabled}
                    />
                    <ActionBarComponent
                        onKeepPackage={onKeepPackage}
                        onDowngrade={setPackageOne}
                        useStandardActionBar={isPackageChangeDowngradeEnabled}
                    />
                </div>
            </div>
        );
    }
}
